/**
 * LegacyImportBanner — cabinet seulement (Batch #14.275 / #14.277) : anciennes factures / dépenses (modules supprimés) à importer
 * dans Ventes · Encaissements · Achats · Décaissements, nouveaux paiements reçus sur d'anciennes factures, pièces annulées à régulariser.
 * L'import tourne aussi automatiquement (démarrage du backend, paiement portail / Stripe, synchronisation périodique).
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { errMsg } from "../comptabilite/_shared";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : "");

function describe(kind, st) {
  const customer = kind === "customer";
  const parts = [];
  const docs = customer ? st.invoices_pending : st.expenses_pending;
  const pays = customer ? st.invoice_payments_pending : st.expense_payments_pending;
  const stale = customer ? st.sales_stale : st.purchases_stale;
  if (docs) parts.push(`${docs} ${customer ? "facture(s) de l'ancien module Factures" : "dépense(s) de l'ancien module Dépenses"} à importer`);
  if (pays) parts.push(`${pays} ${customer ? "paiement(s) reçu(s) sur d'anciennes factures" : "paiement(s) de dépenses"} à importer`);
  if (stale) parts.push(`${stale} pièce(s) annulée(s) ou supprimée(s) dans l'ancien module à régulariser`);
  return { parts, total: docs + pays + stale };
}

export default function LegacyImportBanner({ kind, refresh }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/gl/legacy-import"); setSt(r.data); } catch { setSt(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gl/legacy-import");
      const nb = (kind === "customer" ? r.data.sales + (r.data.sale_credits || 0) + r.data.receipts : r.data.purchases + r.data.disbursements) + (r.data.cancelled || 0) + (r.data.realigned || 0);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} pièce(s) à vérifier — voir le détail`); else toast.success(`${nb} pièce(s) importée(s), réalignée(s) ou régularisée(s)`);
      setShowErrors(Boolean(r.data.errors?.length));
      await load(); refresh?.();
    } catch (e) { toast.error(errMsg(e, "Import impossible")); } finally { setBusy(false); }
  };

  if (!st) return null;
  const { parts, total } = describe(kind, st);
  const errors = st.last_import?.errors || [];
  const orphans = kind === "customer" ? st.invoices_unimportable : 0;
  if (!total && !errors.length && !orphans) return null;

  return (
    <div className={`border rounded-xl p-3 sm:p-4 ${total ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid={`legacy-import-banner-${kind}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {total ? <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-[220px] text-sm">
          {total ? (
            <p className="text-amber-900"><strong data-testid="legacy-import-pending">{total}</strong> à traiter : {parts.join(" · ")}. Les anciennes données ne sont pas perdues : elles sont toujours en base.</p>
          ) : (
            <p className="text-emerald-900">
              Anciennes pièces importées{st.last_import?.at ? ` le ${fmtDate(st.last_import.at)}` : ""}.
              {kind === "customer" && st.last_import?.sale_credits > 0 && ` ${st.last_import.sale_credits} facture(s) négative(s) importée(s) en note(s) de crédit.`}
              {kind === "supplier" && st.last_import?.realigned > 0 && ` ${st.last_import.realigned} achat(s) réaligné(s) sur l'ancienne dépense (qui fait foi).`}
            </p>
          )}
          {orphans > 0 && (
            <p className="mt-1 text-xs text-amber-800" data-testid="legacy-import-unimportable">
              {orphans} facture(s) dont le client n'a pas de dossier Master : créez le dossier (Cabinet › Master Client) puis relancez l'import.
            </p>
          )}
          {errors.length > 0 && (
            <button onClick={() => setShowErrors((v) => !v)} className="mt-1 text-xs underline text-rose-700" data-testid="legacy-import-errors-toggle">
              {errors.length} pièce(s) à vérifier — {showErrors ? "masquer" : "voir le détail"}
            </button>
          )}
          {showErrors && errors.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-rose-800 list-disc pl-4" data-testid="legacy-import-errors">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
        {(total > 0 || orphans > 0 || errors.length > 0) && (
          <button onClick={run} disabled={busy} data-testid="legacy-import-run-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-60 transition active:scale-95">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {total ? "Importer maintenant" : "Relancer l'import"}
          </button>
        )}
      </div>
    </div>
  );
}
